import { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send, Bot } from "lucide-react";
import { API_BASE } from "@/lib/api";

export const AIChat = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sid, setSid] = useState(null);
  const [msgs, setMsgs] = useState([
    { role: "bot", text: "🙏 Namaste! I'm your Next Gen Properties assistant. Ask me about our open plots, prices, approvals or your budget — I'll recommend the best plot for you!" },
  ]);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }, { role: "bot", text: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sid }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        let chunk = decoder.decode(value, { stream: true });
        const sidMatch = chunk.match(/^__SID__(.*?)__\n/);
        if (sidMatch) { setSid(sidMatch[1]); chunk = chunk.replace(sidMatch[0], ""); }
        acc += chunk;
        setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "bot", text: acc }; return c; });
      }
    } catch {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "bot", text: "Sorry, please call +91 99519 71450." }; return c; });
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-40 grid place-items-center w-14 h-14 rounded-full bg-[#0B5D3B] text-white shadow-lg hover:scale-105 transition-transform"
      >
        {open ? <X size={24} /> : <MessageSquare size={24} />}
      </button>

      {open && (
        <div data-testid="ai-chat-window" className="fixed bottom-24 right-6 z-40 w-[92vw] max-w-[380px] h-[520px] bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden">
          <div className="bg-[#0B5D3B] text-white px-5 py-4 flex items-center gap-3">
            <div className="grid place-items-center w-9 h-9 rounded-full bg-white/15"><Bot size={18} /></div>
            <div>
              <p className="font-heading font-semibold text-sm">AI Property Assistant</p>
              <p className="text-[11px] text-white/70">Instant answers · 24/7</p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 hide-scrollbar bg-[#f8faf9]">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#0B5D3B] text-white rounded-br-sm" : "bg-white border border-gray-100 text-gray-800 rounded-bl-sm"}`}>
                  {m.text || (busy && i === msgs.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-gray-100 flex items-center gap-2">
            <input
              data-testid="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about plots, budget, EMI…"
              className="flex-1 px-3.5 py-2.5 rounded-full bg-gray-100 text-sm outline-none focus:ring-2 focus:ring-[#0B5D3B]"
            />
            <button data-testid="ai-chat-send" onClick={send} disabled={busy} className="grid place-items-center w-10 h-10 rounded-full bg-[#0B5D3B] text-white disabled:opacity-50">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
